/**
 * BadgeIcon - Generates unique inline SVG badges based on badge properties.
 * Each badge gets a distinctive visual based on its category, rarity, and type.
 */

interface BadgeIconProps {
  iconUrl?: string | null
  category: string
  rarity?: string
  requirementType: string
  requirementValue: number
  earned?: boolean
  size?: number
}

// Rarity color palettes
const palettes: Record<string, { ring: string; fill: string; accent: string; bg: string }> = {
  common: { ring: '#94a3b8', fill: '#e2e8f0', accent: '#64748b', bg: '#f8fafc' },
  rare: { ring: '#3b82f6', fill: '#dbeafe', accent: '#1d4ed8', bg: '#eff6ff' },
  epic: { ring: '#8b5cf6', fill: '#ede9fe', accent: '#6d28d9', bg: '#f5f3ff' },
  legendary: { ring: '#f59e0b', fill: '#fef3c7', accent: '#d97706', bg: '#fffbeb' },
}

// Category gradient colors (for the inner circle)
const categoryGradients: Record<string, [string, string]> = {
  progress: ['#3b82f6', '#1d4ed8'],
  streak: ['#f97316', '#dc2626'],
  workshop: ['#10b981', '#059669'],
  excellence: ['#a855f7', '#7c3aed'],
  exam: ['#ef4444', '#b91c1c'],
}

// Extract badge slug from icon_url like "/badges/first-step.svg" → "first-step"
function getBadgeSlug(iconUrl?: string | null): string {
  if (!iconUrl) return 'unknown'
  const match = iconUrl.match(/\/badges\/(.+)\.svg$/)
  return match ? match[1] : 'unknown'
}

// Render the center icon based on badge type
function CenterIcon({ slug, value, accent }: { slug: string; value: number; accent: string }) {
  // Lesson badges: show the number prominently
  if (slug.startsWith('lesson-')) {
    const num = Number.parseInt(slug.replace('lesson-', ''), 10) || value
    return (
      <g>
        <text x="50" y="54" textAnchor="middle" dominantBaseline="central"
          fontSize={num >= 10 ? '28' : '32'} fontWeight="800" fill="white" fontFamily="system-ui, sans-serif">
          {num}
        </text>
        {/* Small book icon below */}
        <g transform="translate(38, 65) scale(0.5)">
          <rect x="2" y="0" width="20" height="16" rx="1" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" />
          <line x1="12" y1="0" x2="12" y2="16" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" />
        </g>
      </g>
    )
  }

  switch (slug) {
    case 'first-step':
      return (
        <g transform="translate(50, 50)" fill="white">
          {/* Footprint */}
          <ellipse cx="-5" cy="2" rx="7" ry="12" />
          <circle cx="-12" cy="-13" r="3" />
          <circle cx="-6" cy="-15" r="3" />
          <circle cx="0" cy="-13" r="3" />
        </g>
      )
    case 'dedicated':
      return (
        <g transform="translate(30, 30)" fill="white">
          {/* Book with checkmark */}
          <rect x="5" y="2" width="30" height="36" rx="2" fill="none" stroke="white" strokeWidth="2.5" />
          <line x1="20" y1="2" x2="20" y2="38" stroke="white" strokeWidth="1.5" />
          <polyline points="12,22 17,27 28,16" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )
    case 'unit-master':
      return (
        <g transform="translate(50, 48)" fill="white">
          {/* Trophy */}
          <path d="M-12,-15 h24 v12 c0,8-5,14-12,14 s-12,-6-12,-14z" fill="none" stroke="white" strokeWidth="2.5" />
          <rect x="-3" y="11" width="6" height="6" fill="white" />
          <rect x="-8" y="17" width="16" height="3" rx="1.5" fill="white" />
          <path d="M-12,-15 c-6,0-10,5-10,10 s4,10 10,10" fill="none" stroke="white" strokeWidth="2" />
          <path d="M12,-15 c6,0 10,5 10,10 s-4,10-10,10" fill="none" stroke="white" strokeWidth="2" />
        </g>
      )
    case 'course-champion':
      return (
        <g transform="translate(50, 52)" fill="white">
          {/* Crown */}
          <polygon points="-20,8 -15,-12 -7,0 0,-18 7,0 15,-12 20,8" fill="white" />
          <rect x="-20" y="8" width="40" height="6" rx="2" fill="white" />
          <circle cx="-15" cy="-12" r="3" fill={accent} />
          <circle cx="0" cy="-18" r="3" fill={accent} />
          <circle cx="15" cy="-12" r="3" fill={accent} />
        </g>
      )
    case 'streak-7':
      return (
        <g transform="translate(50, 48)">
          {/* Flame */}
          <path d="M0,-22 c-4,12-16,16-16,28 c0,11 7,20 16,20 s16,-9 16,-20 c0,-12-12,-16-16,-28z"
            fill="white" opacity="0.9" />
          <path d="M0,-8 c-2,7-9,9-9,16 c0,6 4,11 9,11 s9,-5 9,-11 c0,-7-7,-9-9,-16z"
            fill={accent} opacity="0.6" />
          <text x="0" y="10" textAnchor="middle" fontSize="14" fontWeight="800" fill={accent} fontFamily="system-ui">7</text>
        </g>
      )
    case 'streak-30':
      return (
        <g transform="translate(50, 48)">
          {/* Flame */}
          <path d="M0,-22 c-4,12-16,16-16,28 c0,11 7,20 16,20 s16,-9 16,-20 c0,-12-12,-16-16,-28z"
            fill="white" opacity="0.9" />
          <path d="M0,-8 c-2,7-9,9-9,16 c0,6 4,11 9,11 s9,-5 9,-11 c0,-7-7,-9-9,-16z"
            fill={accent} opacity="0.6" />
          <text x="0" y="12" textAnchor="middle" fontSize="12" fontWeight="800" fill={accent} fontFamily="system-ui">30</text>
        </g>
      )
    case 'first-workshop':
      return (
        <g transform="translate(50, 48)" fill="white">
          {/* Single person */}
          <circle cx="0" cy="-12" r="8" />
          <path d="M-12,14 c0,-10 5,-16 12,-16 s12,6 12,16z" />
        </g>
      )
    case 'workshop-expert':
      return (
        <g transform="translate(50, 50)" fill="white">
          {/* Two people */}
          <circle cx="-10" cy="-12" r="6" />
          <circle cx="10" cy="-12" r="6" />
          <path d="M-20,10 c0,-8 4,-13 10,-13 s10,5 10,13z" />
          <path d="M0,10 c0,-8 4,-13 10,-13 s10,5 10,13z" />
          {/* Star above */}
          <polygon points="0,-26 2,-21 7,-21 3,-18 5,-13 0,-16 -5,-13 -3,-18 -7,-21 -2,-21"
            fill="white" />
        </g>
      )
    case 'perfect-score':
      return (
        <g transform="translate(50, 50)">
          {/* Star with 100 */}
          <polygon points="0,-22 6,-8 22,-8 10,2 14,18 0,10 -14,18 -10,2 -22,-8 -6,-8"
            fill="white" opacity="0.3" />
          <text x="0" y="4" textAnchor="middle" dominantBaseline="central"
            fontSize="18" fontWeight="800" fill="white" fontFamily="system-ui">
            100
          </text>
        </g>
      )
    case 'first-exam':
      return (
        <g transform="translate(50, 48)" fill="white">
          {/* Paper/scroll with check */}
          <rect x="-12" y="-18" width="24" height="32" rx="2" fill="none" stroke="white" strokeWidth="2.5" />
          <line x1="-6" y1="-10" x2="6" y2="-10" stroke="white" strokeWidth="2" />
          <line x1="-6" y1="-4" x2="6" y2="-4" stroke="white" strokeWidth="2" />
          <polyline points="-5,6 0,11 10,1" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )
    case 'graduate':
      return (
        <g transform="translate(50, 48)" fill="white">
          {/* Graduation cap */}
          <polygon points="0,-18 28,-6 0,6 -28,-6" fill="white" />
          <path d="M-16,-2 v16 l16,8 16,-8 v-16" fill="none" stroke="white" strokeWidth="2" />
          <line x1="24" y1="-4" x2="24" y2="16" stroke="white" strokeWidth="2" />
          <circle cx="24" cy="18" r="2" fill="white" />
        </g>
      )
    default:
      return (
        <g transform="translate(50, 50)">
          {/* Generic award */}
          <polygon points="0,-20 5,-7 19,-7 8,2 12,16 0,9 -12,16 -8,2 -19,-7 -5,-7"
            fill="white" />
        </g>
      )
  }
}

// Decorative elements based on rarity
function RarityDecoration({ rarity, ringColor }: { rarity: string; ringColor: string }) {
  switch (rarity) {
    case 'rare':
      // Double ring
      return <circle cx="50" cy="50" r="44" fill="none" stroke={ringColor} strokeWidth="1" opacity="0.5" />
    case 'epic':
      // 6 small diamonds around the ring
      return (
        <g>
          {[0, 60, 120, 180, 240, 300].map(angle => {
            const rad = (angle * Math.PI) / 180
            const x = 50 + 46 * Math.cos(rad)
            const y = 50 + 46 * Math.sin(rad)
            return (
              <polygon key={angle}
                points={`${x},${y - 4} ${x + 3},${y} ${x},${y + 4} ${x - 3},${y}`}
                fill={ringColor} />
            )
          })}
        </g>
      )
    case 'legendary':
      // 8 star points around the ring + outer glow
      return (
        <g>
          <circle cx="50" cy="50" r="48" fill="none" stroke={ringColor} strokeWidth="1" opacity="0.3" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
            const rad = (angle * Math.PI) / 180
            const x = 50 + 46 * Math.cos(rad)
            const y = 50 + 46 * Math.sin(rad)
            return (
              <polygon key={angle}
                points={`${x},${y - 5} ${x + 3},${y} ${x},${y + 5} ${x - 3},${y}`}
                fill={ringColor} />
            )
          })}
        </g>
      )
    default:
      return null
  }
}

export default function BadgeIcon({
  iconUrl,
  category,
  rarity = 'common',
  requirementType: _requirementType,
  requirementValue,
  earned = false,
  size = 64,
}: BadgeIconProps) {
  const slug = getBadgeSlug(iconUrl)
  const palette = palettes[rarity] || palettes.common
  const gradient = categoryGradients[category] || categoryGradients.progress
  const gradientId = `bg-${slug}-${rarity}`

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: earned ? undefined : 'grayscale(100%) opacity(0.4)' }}
    >
      <defs>
        <radialGradient id={gradientId} cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor={gradient[0]} />
          <stop offset="100%" stopColor={gradient[1]} />
        </radialGradient>
        {rarity === 'legendary' && (
          <filter id={`glow-${slug}`}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Outer ring */}
      <circle cx="50" cy="50" r="46" fill="none" stroke={palette.ring} strokeWidth="3"
        filter={rarity === 'legendary' ? `url(#glow-${slug})` : undefined} />

      {/* Inner filled circle */}
      <circle cx="50" cy="50" r="42" fill={`url(#${gradientId})`} />

      {/* Subtle inner shadow */}
      <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />

      {/* Rarity decorations */}
      <RarityDecoration rarity={rarity} ringColor={palette.ring} />

      {/* Center icon */}
      <CenterIcon slug={slug} value={requirementValue} accent={gradient[1]} />
    </svg>
  )
}
